import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isSuperAdmin, canRecordContributions } from '../../utils/roles';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TodayContributionsList from './components/TodayContributionsList';
import ContributionForm from './components/ContributionForm';
import ImportSavingsModal from './components/ImportSavingsModal';
import PendingBatchList from './components/PendingBatchList';
import type { Member, ContributionEntry, PendingContribution, ContributionFormData } from './types';
import { Filter, Search, X } from 'lucide-react';

const ContributionRecording = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null);
  const [contributionsEnabledForGroup, setContributionsEnabledForGroup] = useState<boolean>(true);
  const [availableGroups, setAvailableGroups] = useState<Array<{ id: string; name: string }>>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [contributions, setContributions] = useState<ContributionEntry[]>([]);
  const [showRecordingForm, setShowRecordingForm] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  
  // Batch recording state (for clerks)
  const [pendingContributions, setPendingContributions] = useState<PendingContribution[]>([]);
  const [editingContributionId, setEditingContributionId] = useState<string | null>(null);
  const [isSubmittingBatch, setIsSubmittingBatch] = useState(false);
  
  // Check if user is a clerk (should use batch mode)
  const isClerk = userProfile?.group_roles?.includes('clerk');
  
  // Filter states - default to 'pending' for admins to see contributions needing approval
  // For superadmin, default to 'all' to see all contributions
  // If coming from notification, ensure we show pending
  // Note: group_creator can view but cannot record contributions
  const canRecord = canRecordContributions(userProfile?.role) || userProfile?.group_roles?.includes('admin');
  const isGroupCreator = userProfile?.role === 'group_creator';
  const isAdminUser = canRecord || isGroupCreator; // Group creator can view but not record
  const isSuperAdminUser = isSuperAdmin(userProfile?.role);
  const contributionIdFromUrl = searchParams.get('contributionId');
  const fromNotification = searchParams.get('from') === 'notification';
  const [statusFilter, setStatusFilter] = useState<'all' | 'verified' | 'approved' | 'pending'>(
    fromNotification ? 'pending' : (isSuperAdminUser ? 'all' : (isAdminUser ? 'pending' : 'all'))
  );
  const [dateRangeFilter, setDateRangeFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [memberFilter, setMemberFilter] = useState<string>('all');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Ensure status filter is set to 'pending' when coming from notification
  useEffect(() => {
    if (fromNotification && statusFilter !== 'pending') {
      setStatusFilter('pending');
      console.log('[ContributionRecording] Setting filter to pending from notification');
    }
  }, [fromNotification, statusFilter]);

  // Fetch all accessible groups for admin (or all groups for superadmin)
  useEffect(() => {
    if (!user || !isAdminUser) return;

    const fetchAvailableGroups = async () => {
      try {
        let groupsList: Array<{ id: string; name: string }> = [];
        
        if (isSuperAdminUser) {
          // For superadmin, fetch ALL groups
          const { data: allGroups, error: groupsError } = await supabase
            .from('groups')
            .select('id, name')
            .eq('is_active', true)
            .order('created_at', { ascending: false });
          
          if (groupsError) {
            console.error('[ContributionRecording] Error fetching all groups:', groupsError);
          }
          
          groupsList = (allGroups || []).map((g: any) => ({
            id: g.id,
            name: g.name || 'Unnamed Group',
          }));
          
          console.log(`[ContributionRecording] Fetched ${groupsList.length} groups for superadmin`);
          
          // Also extract unique group IDs from contributions to ensure we have all groups with contributions
          // This will be done after contributions are loaded, but we can also do it here as a fallback
        } else {
          // Get all groups user has access to
          const [{ data: createdGroups }, { data: assignedGroups }] = await Promise.all([
            supabase
              .from('groups')
              .select('id, name')
              .eq('created_by', user.id)
              .eq('is_active', true)
              .order('created_at', { ascending: false }),
            supabase
              .from('group_members')
              .select('group_id')
              .eq('user_id', user.id)
              .in('role', ['admin', 'clerk'])
          ]);

          const groupIds = Array.from(
            new Set([
              ...(createdGroups || []).map((g: any) => g.id),
              ...(assignedGroups || []).map((m: any) => m.group_id).filter(Boolean),
            ])
          );

          groupsList = (createdGroups || []).map((g: any) => ({
            id: g.id,
            name: g.name || 'Unnamed Group',
          }));

          // Fetch names for assigned groups not in created list
          const missingIds = groupIds.filter((id) => !groupsList.some((g) => g.id === id));
          if (missingIds.length > 0) {
            const { data: extraGroups } = await supabase
              .from('groups')
              .select('id, name')
              .in('id', missingIds)
              .eq('is_active', true);

            if (extraGroups) {
              groupsList = [
                ...groupsList,
                ...extraGroups.map((g: any) => ({
                  id: g.id,
                  name: g.name || 'Unnamed Group',
                })),
              ];
            }
          }
        }

        setAvailableGroups(groupsList);
        console.log(`[ContributionRecording] Available groups set: ${groupsList.length} groups`);
        
        // For superadmin, don't auto-select a group (they can see all groups)
        // For other admins, select the first group if none is selected
        if (!isSuperAdmin && !currentGroupId && groupsList.length > 0) {
          // Try to find a group with pending contributions
          const { data: pendingGroup } = await supabase
            .from('contributions')
            .select('group_id')
            .eq('status', 'pending_approval')
            .in('group_id', groupsList.map(g => g.id))
            .limit(1)
            .maybeSingle();
          
          if (pendingGroup?.group_id) {
            setCurrentGroupId(pendingGroup.group_id);
          } else {
            setCurrentGroupId(groupsList[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching available groups:', error);
      }
    };

    fetchAvailableGroups();
  }, [user, isAdminUser, currentGroupId]);

  // When a group is selected, fetch whether contributions are enabled for that group
  useEffect(() => {
    if (!currentGroupId) {
      setContributionsEnabledForGroup(true);
      return;
    }
    const fetchGroupContributionsEnabled = async () => {
      const { data, error } = await supabase
        .from('groups')
        .select('contributions_enabled')
        .eq('id', currentGroupId)
        .single();
      if (!error && data) {
        setContributionsEnabledForGroup(data.contributions_enabled !== false);
      } else {
        setContributionsEnabledForGroup(true);
      }
    };
    fetchGroupContributionsEnabled();
  }, [currentGroupId]);

  // Fetch group, members, and full contribution history
  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      if (!user || !userProfile) {
        if (isMounted) setLoading(false);
        return;
      }

      try {
        if (isMounted) setLoading(true);
        
        // Check if user is superadmin early
        const isSuperAdmin = userProfile?.role === 'super_admin';

        // Get user's group (either created by user or user is assigned admin/clerk)
        let groupId: string | null = null;

        // If we have a contributionId from URL (from notification), try to get group_id from that contribution first
        if (contributionIdFromUrl) {
          const { data: contribData, error: contribError } = await supabase
            .from('contributions')
            .select('group_id')
            .eq('id', contributionIdFromUrl)
            .maybeSingle();

          if (!contribError && contribData?.group_id) {
            // Verify user has access to this group
            const { data: membershipCheck } = await supabase
              .from('group_members')
              .select('group_id')
              .eq('group_id', contribData.group_id)
              .eq('user_id', user.id)
              .in('role', ['admin', 'clerk'])
              .maybeSingle();

            if (membershipCheck || canRecordContributions(userProfile?.role)) {
              groupId = contribData.group_id;
              console.log(`[ContributionRecording] Found group ${groupId} from contribution ${contributionIdFromUrl}`);
            }
          }
        }

        // If we don't have a group yet, try standard methods
        if (!groupId) {
          // First try: Find a group with pending contributions that user can access (prioritize groups needing approval)
          // Get all groups user has access to
          const { data: accessibleGroups } = await supabase
            .from('groups')
            .select('id')
            .eq('created_by', user.id)
            .eq('is_active', true);

          const { data: assignedGroups } = await supabase
            .from('group_members')
            .select('group_id')
            .eq('user_id', user.id)
            .in('role', ['admin', 'clerk']);

          const allGroupIds = [
            ...(accessibleGroups || []).map((g: any) => g.id),
            ...(assignedGroups || []).map((g: any) => g.group_id)
          ];

          if (allGroupIds.length > 0) {
            // Find a group with pending contributions
            const { data: pendingGroup } = await supabase
              .from('contributions')
              .select('group_id')
              .eq('status', 'pending_approval')
              .in('group_id', allGroupIds)
              .limit(1)
              .maybeSingle();

            if (pendingGroup?.group_id) {
              groupId = pendingGroup.group_id;
              console.log(`[ContributionRecording] Selected group with pending contributions: ${groupId}`);
            }
          }

          // If still no group, use the first accessible group
          if (!groupId) {
            // First try: groups created by user (group_admin)
            const { data: userGroups, error: groupsError } = await supabase
              .from('groups')
              .select('id')
              .eq('created_by', user.id)
              .eq('is_active', true)
              .limit(1)
              .maybeSingle();

            if (!groupsError && userGroups) {
              groupId = userGroups.id;
            } else {
              // Second try: groups where user is assigned admin/clerk (group_members.role in ['admin', 'clerk'])
              const { data: adminGroups, error: adminError } = await supabase
                .from('group_members')
                .select('group_id')
                .eq('user_id', user.id)
                .in('role', ['admin', 'clerk'])
                .limit(1)
                .maybeSingle();

              if (!adminError && adminGroups) {
                groupId = adminGroups.group_id;
              }
            }
          }
        }

        // For superadmin, allow proceeding without a specific group (they can see all groups)
        console.log('[ContributionRecording] Group check - groupId:', groupId, 'isSuperAdmin:', isSuperAdmin, 'userRole:', userProfile?.role);
        if (!groupId && !isSuperAdmin) {
          console.error('No group found for user (role:', userProfile?.role, ', isSuperAdmin:', isSuperAdmin, ')');
          if (isMounted) {
            setLoading(false);
            setCurrentGroupId(null);
          }
          return;
        }
        
        // For superadmin without a group, we can still proceed to fetch all contributions
        if (!groupId && isSuperAdmin) {
          console.log('[ContributionRecording] Superadmin accessing all groups - no specific group selected');
        }

        if (isMounted) {
          setCurrentGroupId(groupId); // Can be null for superadmin
        }

        // Fetch group members with their profiles (skip for superadmin if no group selected)
        let groupMembers: any[] = [];
        
        if (groupId) {
          const { data, error: membersError } = await supabase
            .from('group_members')
            .select(`
              user_id,
              user_profiles (
                id,
                full_name,
                email,
                avatar_url
              )
            `)
            .eq('group_id', groupId);
          
          groupMembers = data || [];
          if (membersError) {
            console.error('Error fetching group members:', membersError);
          }
        }

        // Fetch contributions - get ALL contributions for the group (or all groups for superadmin)
        let allContributions: any[] = [];
        let contribError: any = null;

        try {
          // For superadmin, fetch from all groups; otherwise fetch from selected group
          const isSuperAdmin = userProfile?.role === 'super_admin';
          
          // First, let's check if there are any contributions at all
          let countQuery = supabase
            .from('contributions')
            .select('*', { count: 'exact', head: true });
          
          if (!isSuperAdmin) {
            countQuery = countQuery.eq('group_id', groupId);
          }
          
          const { count: totalCount } = await countQuery;
          console.log(`[ContributionRecording] Total contributions: ${totalCount}`);

          // Now check specifically for pending_approval
          let pendingQuery = supabase
            .from('contributions')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'pending_approval');
          
          if (!isSuperAdmin) {
            pendingQuery = pendingQuery.eq('group_id', groupId);
          }
          
          const { count: pendingCount } = await pendingQuery;
          console.log(`[ContributionRecording] Pending approval contributions: ${pendingCount}`);

          let query = supabase
            .from('contributions')
            .select(`
              id,
              member_id,
              amount,
              contribution_date,
              notes,
              status,
              recorded_by,
              group_id,
              group:groups!contributions_group_id_fkey (
                id,
                name
              ),
              member:user_profiles!contributions_member_id_fkey (
                id,
                full_name,
                avatar_url
              ),
              recorder:user_profiles!contributions_recorded_by_fkey (
                id,
                full_name
              )
            `);
          
          if (!isSuperAdmin && groupId) {
            query = query.eq('group_id', groupId);
          }
          
          console.log(`[ContributionRecording] Executing query${isSuperAdmin ? ' (all groups)' : ` (group: ${groupId})`}`);
          const { data, error } = await query.order('contribution_date', { ascending: false });
          
          if (error) {
            console.error('[ContributionRecording] Query error:', error);
          } else {
            console.log(`[ContributionRecording] Query returned ${data?.length || 0} contributions`);
          }
          
          allContributions = data || [];
          contribError = error;
        } catch (fkError) {
          console.warn('Foreign key relationship failed, fetching without relationships:', fkError);
          const isSuperAdmin = userProfile?.role === 'super_admin';
          
          let query = supabase
            .from('contributions')
            .select('id, member_id, amount, contribution_date, notes, status, recorded_by, group_id');
          
          if (!isSuperAdmin) {
            query = query.eq('group_id', groupId);
          }
          
          const { data, error } = await query.order('contribution_date', { ascending: false });
          
          allContributions = data || [];
          contribError = error;

          if (allContributions.length > 0) {
            const userIds = [...new Set([
              ...allContributions.map(c => c.member_id).filter(Boolean),
              ...allContributions.map(c => c.recorded_by).filter(Boolean)
            ])];

            // Fetch group names for contributions
            const groupIds = [...new Set(allContributions.map(c => c.group_id).filter(Boolean))];
            let groupsMap: Record<string, { id: string; name: string }> = {};
            
            if (groupIds.length > 0) {
              const { data: groups } = await supabase
                .from('groups')
                .select('id, name')
                .in('id', groupIds);
              
              if (groups) {
                groupsMap = groups.reduce((acc, g) => {
                  acc[g.id] = { id: g.id, name: g.name || 'Unknown Group' };
                  return acc;
                }, {} as Record<string, { id: string; name: string }>);
              }
            }

            if (userIds.length > 0) {
              const { data: profiles } = await supabase
                .from('user_profiles')
                .select('id, full_name, avatar_url')
                .in('id', userIds);

              allContributions = allContributions.map(contrib => ({
                ...contrib,
                member: profiles?.find((p: any) => p.id === contrib.member_id),
                recorder: profiles?.find((p: any) => p.id === contrib.recorded_by),
                group: contrib.group_id ? groupsMap[contrib.group_id] : null,
              }));
            } else {
              // Even if no user profiles, still add group info
              allContributions = allContributions.map(contrib => ({
                ...contrib,
                group: contrib.group_id ? groupsMap[contrib.group_id] : null,
              }));
            }
          }
        }

        if (contribError) {
          console.error('Error fetching contributions:', contribError);
          allContributions = [];
        }

        // Debug: Log contributions for troubleshooting
        if (isMounted) {
          if (allContributions.length > 0) {
            const pendingCount = allContributions.filter((c: any) => 
              c.status === 'pending_approval' || c.status === 'pending'
            ).length;
            const statusBreakdown = allContributions.reduce((acc: any, c: any) => {
              acc[c.status] = (acc[c.status] || 0) + 1;
              return acc;
            }, {});
            console.log(`[ContributionRecording] Loaded ${allContributions.length} contributions${groupId ? ` for group ${groupId}` : ' (all groups for superadmin)'}`);
            console.log(`[ContributionRecording] Status breakdown:`, statusBreakdown);
            console.log(`[ContributionRecording] ${pendingCount} pending approval`);
          } else {
            if (groupId) {
              console.log(`[ContributionRecording] No contributions found for group ${groupId}`);
              // Try to verify group exists and user has access
              const { data: groupCheck } = await supabase
                .from('groups')
                .select('id, name, created_by')
                .eq('id', groupId)
                .maybeSingle();
              console.log(`[ContributionRecording] Group check:`, groupCheck);
              
              const { data: membershipCheck } = await supabase
                .from('group_members')
                .select('user_id, role')
                .eq('group_id', groupId)
                .eq('user_id', user.id)
                .maybeSingle();
              console.log(`[ContributionRecording] Membership check:`, membershipCheck);
            } else if (isSuperAdmin) {
              console.log(`[ContributionRecording] Superadmin: No contributions found across all groups`);
            }
          }
        }

        // Get group creator ID(s) to exclude from member list
        let groupCreatorIds: Set<string> = new Set();
        if (groupId) {
          // Get creator for the specific group
          const { data: groupInfo } = await supabase
            .from('groups')
            .select('created_by')
            .eq('id', groupId)
            .maybeSingle();
          if (groupInfo?.created_by) {
            groupCreatorIds.add(groupInfo.created_by);
          }
        } else if (isSuperAdmin) {
          // For superadmin viewing all groups, get all group creators
          const { data: allGroups } = await supabase
            .from('groups')
            .select('created_by')
            .eq('is_active', true);
          if (allGroups) {
            allGroups.forEach(g => {
              if (g.created_by) groupCreatorIds.add(g.created_by);
            });
          }
        }

        // Map members (excluding group creators)
        let membersData: Member[] = [];
        
        if (isSuperAdmin && !groupId) {
          // For superadmin without a specific group, extract unique members from contributions
          const uniqueMemberIds = [...new Set(allContributions.map(c => c.member_id).filter(Boolean))];
          
          console.log(`[ContributionRecording] Extracting ${uniqueMemberIds.length} unique members from contributions`);
          
          if (uniqueMemberIds.length > 0) {
            const { data: profiles, error: profilesError } = await supabase
              .from('user_profiles')
              .select('id, full_name, email, avatar_url')
              .in('id', uniqueMemberIds);
            
            if (profilesError) {
              console.error('[ContributionRecording] Error fetching member profiles:', profilesError);
            }
            
            if (profiles && profiles.length > 0) {
              console.log(`[ContributionRecording] Fetched ${profiles.length} member profiles`);
              membersData = profiles
                .filter((profile: any) => !groupCreatorIds.has(profile.id)) // Exclude group creators
                .map((profile: any) => {
                  const memberContribs = allContributions.filter(c => c.member_id === profile.id);
                  const totalContributions = memberContribs.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
                  
                  const dates = memberContribs
                    .map(c => c.contribution_date ? new Date(c.contribution_date).getTime() : null)
                    .filter((d): d is number => d !== null);
                  const lastContrib = dates.length > 0 ? new Date(Math.max(...dates)) : null;

                  return {
                    id: profile.id,
                    name: profile.full_name || profile.email || 'Unknown',
                    email: profile.email || '',
                    avatar: profile.avatar_url || '',
                    totalContributions,
                    lastContributionDate: lastContrib,
                    isFavorite: false,
                  };
                });
              console.log(`[ContributionRecording] Mapped ${membersData.length} members (excluded ${groupCreatorIds.size} group creators)`);
            } else {
              console.warn('[ContributionRecording] No profiles found for member IDs:', uniqueMemberIds.slice(0, 5), '...');
            }
          } else {
            console.log('[ContributionRecording] No unique member IDs found in contributions');
          }
        } else {
          // For regular users or superadmin with a selected group
          membersData = (groupMembers || [])
            .filter((gm: any) => {
              const profile = Array.isArray(gm.user_profiles) ? gm.user_profiles[0] : gm.user_profiles;
              if (!profile || !profile.id) return false;
              // Exclude group creator
              return !groupCreatorIds.has(profile.id);
            })
            .map((gm: any) => {
              const profile = Array.isArray(gm.user_profiles) ? gm.user_profiles[0] : gm.user_profiles;
              if (!profile || !profile.id) return null;

              const memberContribs = allContributions.filter(c => c.member_id === profile.id);
              const totalContributions = memberContribs.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
              
              const dates = memberContribs
                .map(c => c.contribution_date ? new Date(c.contribution_date).getTime() : null)
                .filter((d): d is number => d !== null);
              const lastContrib = dates.length > 0 ? new Date(Math.max(...dates)) : null;

              return {
                id: profile.id,
                name: profile.full_name || profile.email || 'Unknown',
                email: profile.email || '',
                avatar: profile.avatar_url || '',
                totalContributions,
                lastContributionDate: lastContrib,
                isFavorite: false,
              };
            })
            .filter((m): m is Member => m !== null);
          
          console.log(`[ContributionRecording] Mapped ${membersData.length} members (excluded ${groupCreatorIds.size} group creator(s))`);
        }

        if (isMounted) {
          setMembers(membersData);

          const contributionsData: ContributionEntry[] = (allContributions || [])
            .map((contrib: any) => {
              const date = contrib.contribution_date ? new Date(contrib.contribution_date) : new Date();
              // Only superadmin can edit contributions
              const canEdit = userProfile?.role === 'super_admin';
              const group = Array.isArray(contrib.group) ? contrib.group[0] : contrib.group;
              return {
                id: contrib.id || '',
                memberId: contrib.member_id || '',
                memberName: contrib.member?.full_name || contrib.member?.email || 'Unknown',
                memberAvatar: contrib.member?.avatar_url || '',
                amount: parseFloat(contrib.amount) || 0,
                date: date,
                recordedBy: contrib.recorder?.full_name || contrib.recorder?.email || 'Unknown',
                recordedById: contrib.recorded_by || '',
                notes: contrib.notes || '',
                status: (
                  contrib.status === 'approved' ? 'approved' : 
                  (contrib.status === 'pending_approval' || contrib.status === 'pending') ? 'pending' : 'verified'
                ) as 'verified' | 'approved' | 'pending',
                canEdit: canEdit,
                timestamp: date,
                groupId: contrib.group_id || group?.id || '',
                groupName: group?.name || 'Unknown Group',
              };
            });

          console.log(`[ContributionRecording] Setting ${contributionsData.length} contributions in state`);
          setContributions(contributionsData);
        }
      } catch (error: any) {
        console.error('Error fetching contribution history:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [user, userProfile, contributionIdFromUrl, currentGroupId]);

  // Filter contributions
  const filteredContributions = contributions.filter((contrib) => {
    if (statusFilter !== 'all' && contrib.status !== statusFilter) return false;
    if (dateRangeFilter !== 'all') {
      const now = new Date();
      const diffDays = Math.floor((new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - 
        new Date(contrib.date.getFullYear(), contrib.date.getMonth(), contrib.date.getDate()).getTime()) / (1000 * 60 * 60 * 24));
      if (dateRangeFilter === 'today' && diffDays !== 0) return false;
      if (dateRangeFilter === 'week' && (diffDays < 0 || diffDays >= 7)) return false;
      if (dateRangeFilter === 'month' && (diffDays < 0 || diffDays >= 30)) return false;
    }
    if (groupFilter !== 'all' && contrib.groupId !== groupFilter) return false;
    if (memberFilter !== 'all' && contrib.memberId !== memberFilter) return false;
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      return contrib.memberName.toLowerCase().includes(s) || 
             contrib.recordedBy.toLowerCase().includes(s) || 
             contrib.notes.toLowerCase().includes(s) || 
             contrib.amount.toString().includes(s) ||
             (contrib.groupName && contrib.groupName.toLowerCase().includes(s));
    }
    return true;
  });

  const handleApprove = async (contributionId: string) => {
    try {
      const { error } = await supabase
        .from('contributions')
        .update({ status: 'approved' })
        .eq('id', contributionId);

      if (error) throw error;
      
      // Update local state
      setContributions(prev => 
        prev.map(c => c.id === contributionId ? { ...c, status: 'approved' } : c)
      );
      
      alert('Contribution approved successfully');
    } catch (err: any) {
      console.error('Error approving contribution:', err);
      alert('Failed to approve: ' + err.message);
    }
  };

  const handleReject = async (contributionId: string) => {
    if (!window.confirm('Are you sure you want to reject this contribution?')) return;
    
    try {
      const { error } = await supabase
        .from('contributions')
        .update({ status: 'rejected' })
        .eq('id', contributionId);

      if (error) throw error;
      
      // Update local state
      setContributions(prev => 
        prev.map(c => c.id === contributionId ? { ...c, status: 'verified' } : c) // status maps to verified for rejected/others
      );
      
      alert('Contribution rejected');
      window.location.reload(); // Simplest way to refresh since status mapping is complex
    } catch (err: any) {
      console.error('Error rejecting contribution:', err);
      alert('Failed to reject: ' + err.message);
    }
  };

  const handleDeleteContribution = async (contributionId: string) => {
    if (!window.confirm('Are you sure you want to remove this contribution? This action cannot be undone.')) return;
    
    try {
      const { error } = await supabase
        .from('contributions')
        .delete()
        .eq('id', contributionId);
      
      if (error) throw error;
      
      alert('Contribution removed successfully');
      // Remove from local state immediately
      setContributions(prev => prev.filter(c => c.id !== contributionId));
    } catch (err: any) {
      console.error('Error deleting contribution:', err);
      alert('Failed to remove contribution: ' + err.message);
    }
  };

  // Batch recording handlers
  const handleAddToBatch = (formData: ContributionFormData) => {
    if (!user || !currentGroupId) return;

    const member = members.find(m => m.id === formData.memberId);
    if (!member) return;

    const pendingContrib: PendingContribution = {
      id: `pending_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      memberId: formData.memberId,
      memberName: member.name,
      memberAvatar: member.avatar,
      amount: parseFloat(formData.amount),
      date: formData.date,
      notes: formData.notes.trim(),
    };

    setPendingContributions(prev => [...prev, pendingContrib]);
  };

  const handleRemoveFromBatch = (id: string) => {
    setPendingContributions(prev => prev.filter(c => c.id !== id));
    if (editingContributionId === id) {
      setEditingContributionId(null);
    }
  };

  const handleEditBatchItem = (id: string) => {
    const contrib = pendingContributions.find(c => c.id === id);
    if (!contrib) return;
    
    setEditingContributionId(id);
    setShowRecordingForm(true);
  };

  // Handle editing existing contribution (superadmin only)
  const handleEditContribution = (contribution: ContributionEntry) => {
    if (userProfile?.role !== 'super_admin') {
      alert('Only super admins can edit contributions');
      return;
    }
    
    // Set up editing state for existing contribution
    setEditingContributionId(contribution.id);
    setShowRecordingForm(true);
  };

  // Handle updating existing contribution in database (superadmin only)
  const handleUpdateContribution = async (formData: ContributionFormData) => {
    if (userProfile?.role !== 'super_admin') {
      alert('Only super admins can edit contributions');
      return;
    }

    if (!editingContributionId || !user || !currentGroupId) return;

    try {
      const { error } = await supabase
        .from('contributions')
        .update({
          member_id: formData.memberId,
          amount: parseFloat(formData.amount),
          contribution_date: formData.date.toISOString(),
          notes: formData.notes.trim() || null,
          edited_by: user.id,
          edited_at: new Date().toISOString(),
        })
        .eq('id', editingContributionId);

      if (error) throw error;

      alert('Contribution updated successfully');
      setEditingContributionId(null);
      setShowRecordingForm(false);
      window.location.reload(); // Refresh to show updated data
    } catch (err: any) {
      console.error('Error updating contribution:', err);
      alert('Failed to update contribution: ' + err.message);
    }
  };

  const handleUpdateBatchItem = (formData: ContributionFormData) => {
    if (!editingContributionId || !user || !currentGroupId) return;

    const member = members.find(m => m.id === formData.memberId);
    if (!member) return;

    setPendingContributions(prev =>
      prev.map(c =>
        c.id === editingContributionId
          ? {
              ...c,
              memberId: formData.memberId,
              memberName: member.name,
              memberAvatar: member.avatar,
              amount: parseFloat(formData.amount),
              date: formData.date,
              notes: formData.notes.trim(),
            }
          : c
      )
    );

    // Close form after update
    setEditingContributionId(null);
    setShowRecordingForm(false);
  };

  const handleSubmitBatch = async () => {
    if (!user || !currentGroupId || pendingContributions.length === 0) return;

    setIsSubmittingBatch(true);
    try {
      const entriesToInsert = pendingContributions.map(contrib => ({
        group_id: currentGroupId,
        member_id: contrib.memberId,
        amount: contrib.amount,
        contribution_date: contrib.date.toISOString(),
        notes: contrib.notes || null,
        status: 'pending_approval',
        recorded_by: user.id,
      }));

      const { error } = await supabase.from('contributions').insert(entriesToInsert);

      if (error) throw error;

      alert(`Successfully submitted ${pendingContributions.length} contribution(s) for approval.`);
      
      // Clear batch and refresh
      setPendingContributions([]);
      setShowRecordingForm(false);
      window.location.reload();
    } catch (error: any) {
      console.error('Error submitting batch:', error);
      alert('Failed to submit contributions: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSubmittingBatch(false);
    }
  };

  const handleCancelBatch = () => {
    if (pendingContributions.length > 0 && !window.confirm('Are you sure you want to cancel? All pending contributions will be lost.')) {
      return;
    }
    setPendingContributions([]);
    setEditingContributionId(null);
    setShowRecordingForm(false);
  };

  return (
    <>
      <Helmet>
        <title>Contribution Recording - communitySaving</title>
        <meta name="description" content="Record and view contributions for your group" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)} isMobileMenuOpen={isMobileMenuOpen} />

        <div className="flex">
          <div className="hidden lg:block">
            <Sidebar isCollapsed={isSidebarCollapsed} userRole="admin" />
          </div>

          <main className={`flex-1 transition-layout ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
            <div className="p-4 lg:p-6 max-w-7xl mx-auto">
              <div className="mb-6">
                <BreadcrumbTrail />
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex-1">
                    <h1 className="text-2xl lg:text-3xl font-semibold text-foreground mb-2">
                      {showRecordingForm ? (isClerk ? 'Record Savings (Batch Mode)' : 'Record Savings') : 'Contribution History'}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      {showRecordingForm 
                        ? (isClerk 
                            ? 'Record each member\'s contribution. All contributions will be submitted together for approval.'
                            : 'Enter savings details for a member.')
                        : 'View all contributions recorded for your group.'}
                    </p>
                  </div>
                  
                  {/* Group selector for admins with multiple groups */}
                  {isAdminUser && !showRecordingForm && availableGroups.length > 1 && (
                    <div className="min-w-[200px]">
                      <label className="block text-xs font-medium text-foreground mb-2">Select Group</label>
                      <select
                        value={currentGroupId || ''}
                        onChange={(e) => {
                          setCurrentGroupId(e.target.value);
                          setStatusFilter(isAdminUser ? 'pending' : 'all'); // Reset to pending when switching groups
                        }}
                        className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        {availableGroups.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3">
                    {!showRecordingForm && !isGroupCreator && (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => setIsImportModalOpen(true)}
                          iconName="Upload"
                          iconPosition="left"
                          disabled={!contributionsEnabledForGroup}
                        >
                          Import CSV
                        </Button>
                        <Button
                          onClick={() => setShowRecordingForm(true)}
                          iconName="Plus"
                          iconPosition="left"
                          disabled={!contributionsEnabledForGroup}
                        >
                          Record Savings
                        </Button>
                      </>
                    )}
                    <button
                      onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                      className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-sm border border-border hover:bg-muted transition-interactive"
                    >
                      <Icon name={isSidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={18} />
                      <span className="text-sm">{isSidebarCollapsed ? 'Expand' : 'Collapse'}</span>
                    </button>
                  </div>
                </div>
              </div>

              {showRecordingForm && !isGroupCreator ? (
                <div className="max-w-xl mx-auto space-y-6">
                  {!contributionsEnabledForGroup && (
                    <div className="bg-destructive/10 border border-destructive/30 text-destructive rounded-md p-4 flex items-center gap-3">
                      <Icon name="AlertCircle" size={24} />
                      <div>
                        <p className="font-medium">Contributions are disabled for this group</p>
                        <p className="text-sm opacity-90">A group admin or creator has turned off contribution recording. You can still view and approve existing contributions.</p>
                      </div>
                    </div>
                  )}
                  {contributionsEnabledForGroup && (
                  <div className="bg-card border border-border rounded-md p-6 lg:p-8">
                    <div className="mb-4">
                      <h2 className="text-lg font-semibold text-foreground mb-2">
                        {editingContributionId 
                          ? (editingContributionId.startsWith('pending_') 
                              ? 'Edit Batch Item' 
                              : 'Edit Contribution')
                          : (isClerk ? 'Add Contribution to Batch' : 'Record Contribution')}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {editingContributionId && !editingContributionId.startsWith('pending_')
                          ? 'Update the contribution details. Only super admins can edit contributions.'
                          : isClerk 
                            ? 'Record each member\'s contribution. All contributions will be submitted together for approval.'
                            : 'Record a member\'s contribution.'}
                      </p>
                    </div>
                    <ContributionForm
                      groupId={currentGroupId || ''}
                      members={members}
                      onSuccess={() => {
                        if (!isClerk) {
                          setShowRecordingForm(false);
                          window.location.reload();
                        }
                      }}
                      onCancel={() => {
                        if (editingContributionId) {
                          setEditingContributionId(null);
                        }
                        setShowRecordingForm(false);
                      }}
                      batchMode={isClerk && !editingContributionId?.startsWith('pending_') === false}
                      onAddToBatch={editingContributionId 
                        ? (editingContributionId.startsWith('pending_') 
                            ? handleUpdateBatchItem 
                            : handleUpdateContribution)
                        : handleAddToBatch}
                      initialData={editingContributionId 
                        ? (() => {
                            // Check if editing batch item or existing contribution
                            const batchContrib = pendingContributions.find(c => c.id === editingContributionId);
                            if (batchContrib) {
                              return {
                                memberId: batchContrib.memberId,
                                amount: batchContrib.amount.toString(),
                                date: batchContrib.date,
                                notes: batchContrib.notes,
                              };
                            }
                            // Check if editing existing contribution
                            const existingContrib = contributions.find(c => c.id === editingContributionId);
                            if (existingContrib) {
                              return {
                                memberId: existingContrib.memberId,
                                amount: existingContrib.amount.toString(),
                                date: existingContrib.date,
                                notes: existingContrib.notes || '',
                              };
                            }
                            return undefined;
                          })()
                        : undefined}
                    />
                  </div>
                  )}
                  
                  {isClerk && pendingContributions.length > 0 && (
                    <PendingBatchList
                      contributions={pendingContributions}
                      onRemove={handleRemoveFromBatch}
                      onEdit={handleEditBatchItem}
                      onSubmit={handleSubmitBatch}
                      onCancel={handleCancelBatch}
                      isSubmitting={isSubmittingBatch}
                    />
                  )}
                </div>
              ) : (
                <>
                  {isClerk && pendingContributions.length > 0 && (
                    <div className="mb-6">
                      <PendingBatchList
                        contributions={pendingContributions}
                        onRemove={handleRemoveFromBatch}
                        onEdit={handleEditBatchItem}
                        onSubmit={handleSubmitBatch}
                        onCancel={handleCancelBatch}
                        isSubmitting={isSubmittingBatch}
                      />
                    </div>
                  )}

                  {/* Show pending contributions alert for admins */}
                  {isAdminUser && !showRecordingForm && (() => {
                    const pendingCount = contributions.filter(c => c.status === 'pending').length;
                    if (pendingCount > 0) {
                      return (
                        <div className="mb-6 bg-warning/10 border border-warning/20 rounded-md p-4">
                          <div className="flex items-center gap-3">
                            <Icon name="AlertCircle" size={20} className="text-warning flex-shrink-0" />
                            <div className="flex-1">
                              <h3 className="text-sm font-semibold text-foreground mb-1">
                                {pendingCount} Contribution{pendingCount !== 1 ? 's' : ''} Pending Approval
                              </h3>
                              <p className="text-xs text-muted-foreground">
                                Review and approve or reject contributions recorded by clerks.
                              </p>
                            </div>
                            {statusFilter !== 'pending' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setStatusFilter('pending')}
                                iconName="Filter"
                                iconPosition="left"
                              >
                                Show Pending
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  <div className="mb-6 bg-card border border-border rounded-md p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Filter className="w-5 h-5 text-muted-foreground" />
                      <h3 className="text-sm font-semibold text-foreground">Filters</h3>
                    </div>
                    
                    <div className={`grid grid-cols-1 md:grid-cols-2 ${isSuperAdminUser ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-4`}>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-2">Status</label>
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)} className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                          <option value="all">All Status</option>
                          <option value="pending">Pending Approval {isAdminUser && (() => {
                            const pendingCount = contributions.filter(c => c.status === 'pending').length;
                            return pendingCount > 0 ? `(${pendingCount})` : '';
                          })()}</option>
                          <option value="approved">Approved</option>
                          <option value="verified">Verified</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-foreground mb-2">Date Range</label>
                        <select value={dateRangeFilter} onChange={(e) => setDateRangeFilter(e.target.value as any)} className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                          <option value="all">All Time</option>
                          <option value="today">Today</option>
                          <option value="week">This Week</option>
                          <option value="month">This Month</option>
                        </select>
                      </div>

                      {isSuperAdminUser && (
                        <div>
                          <label className="block text-xs font-medium text-foreground mb-2">Group</label>
                          <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                            <option value="all">All Groups</option>
                            {availableGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                          </select>
                        </div>
                      )}

                      <div>
                        <label className="block text-xs font-medium text-foreground mb-2">Member</label>
                        <select value={memberFilter} onChange={(e) => setMemberFilter(e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                          <option value="all">All Members</option>
                          {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-foreground mb-2">Search</label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search..." className="w-full pl-9 pr-8 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {loading ? (
                    <div className="flex items-center justify-center py-12 text-muted-foreground">Loading...</div>
                  ) : !currentGroupId && !isSuperAdmin ? (
                    <div className="text-center py-12">
                      <Icon name="AlertCircle" size={48} className="text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No group found</p>
                      <p className="text-xs text-muted-foreground mt-2">You need to be assigned to a group as an admin or clerk.</p>
                    </div>
                  ) : contributions.length === 0 ? (
                    <div className="text-center py-12">
                      <Icon name="DollarSign" size={48} className="text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No contributions found</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {isAdminUser 
                          ? 'No contributions have been recorded yet. Contributions recorded by clerks will appear here for approval.'
                          : 'No contributions have been recorded for this group yet.'}
                      </p>
                    </div>
                  ) : filteredContributions.length === 0 ? (
                    <div className="text-center py-12">
                      <Icon name="Filter" size={48} className="text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No contributions match the current filters</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Try adjusting your filters to see more contributions.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setStatusFilter('all');
                          setDateRangeFilter('all');
                          setMemberFilter('all');
                          setSearchTerm('');
                        }}
                        className="mt-4"
                      >
                        Clear All Filters
                      </Button>
                    </div>
                  ) : (
                    <div className="max-w-3xl mx-auto">
                      <div className="mb-4 text-sm text-muted-foreground">Showing {filteredContributions.length} of {contributions.length} contributions</div>
                      <TodayContributionsList 
                        contributions={filteredContributions} 
                        currentUserId={user?.id || ''} 
                        isAdmin={isAdminUser}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        onEdit={isSuperAdminUser ? handleEditContribution : undefined}
                        onDelete={isSuperAdminUser ? handleDeleteContribution : undefined}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>
      </div>

      <ImportSavingsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        groupId={currentGroupId}
        onSuccess={() => {
          // Refresh data
          window.location.reload();
        }}
      />
    </>
  );
};

export default ContributionRecording;
